import { getOverflowAncestors } from '@floating-ui/react-dom';
import classNames from 'classnames';
import type { ComponentProps } from 'react';
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ErrorBoundary from './ErrorBoundary';
import * as styles from './HoverView.module.scss';
import Slot from './Slot';
import type { TetherRef } from './Tether';
import Tether from './Tether';
import { isTouchDevice } from '../constants/os';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import useControlledState from '../utils/useControlledState';
import type { HoverDelay } from '../utils/useHover';
import useHover from '../utils/useHover';

interface UseOpenManagerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const useOpenManager = ({ open, onOpenChange }: UseOpenManagerProps) => {
  const [isOpen, setIsOpen] = useControlledState({
    defaultValue: false,
    value: open,
    onValueChange: onOpenChange,
  });
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const onIsOpenChange = useCallback(
    (newIsOpen: boolean) => {
      setIsOpen(newIsOpen);
      if (newIsOpen) setHasBeenOpened(true);
    },
    [setIsOpen],
  );

  // Could happen when the component is controlled
  if (isOpen && !hasBeenOpened) {
    setHasBeenOpened(true);
  }

  return { isOpen, hasBeenOpened, onIsOpenChange };
};

const interactiveElementSelector =
  'button:not([disabled]), a[href], [role="button"], [tabindex]';

const useTriggerElement = () => {
  const [triggerElement, originalSetTriggerElement] =
    useState<HTMLElement | null>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    if (!triggerElementRef.current) {
      throw new Error(
        `HoverView: Trigger element not found. This could be because:
          1. The trigger element is a React component that doesn't forward refs
          2. The trigger element is conditionally rendered
          
          Solution: If the trigger element is conditionally rendered, wrap the entire HoverView component in a conditional instead.`,
      );
    }
  }, []);

  const setTriggerElement = useCallback(
    (element: HTMLElement | null) => {
      triggerElementRef.current = element;
      originalSetTriggerElement(element);
    },
    [originalSetTriggerElement],
  );

  return [triggerElement, setTriggerElement] as const;
};

type HoverViewProps = Pick<
  ComponentProps<typeof Tether>,
  'placement' | 'flipBehavior' | 'offset'
> & {
  anchorElement?: HTMLElement | null;
  children: React.ReactNode;
  hoverGroupId?: string;
  hoverDelay?: HoverDelay;
  view: React.ReactNode;
} & (
    | {
        open: boolean;
        onOpenChange?: (open: boolean) => void;
      }
    | {
        open?: never;
        onOpenChange?: never;
      }
  );

/**
 * A headless component that displays a view when hovering over its children.
 * Both the view and the anchor (children) must be able to accept props and pass them
 * down to their underlying HTML elements, since they will receive refs, event handlers, etc.
 *
 * Following the product spec, when the anchor is not interactive, the view also appears
 * when the anchor is clicked.
 *
 * @example
 * <HoverView view={<FloatingCard>Hello</FloatingCard>} placement="top">
 *   <div>Hover over me</div>
 * </HoverView>
 */
const HoverView = ({
  anchorElement: providedAnchorElement,
  children,
  hoverGroupId,
  hoverDelay = 'slow',
  view,
  open,
  onOpenChange,
  placement,
  flipBehavior,
  offset,
}: HoverViewProps) => {
  const id = useId();

  const { isOpen, hasBeenOpened, onIsOpenChange } = useOpenManager({
    open,
    onOpenChange,
  });

  const tetherRef = useRef<TetherRef>(null);
  useLayoutEffect(() => {
    if (isOpen) tetherRef.current?.updatePositionedView();
  }, [isOpen]);

  const {
    onMouseEnter: originalOnMouseEnter,
    onMouseLeave: originalOnMouseLeave,
    setHover,
  } = useHover({
    hoverGroupId,
    hoverDelay,
    onHoverChange(hoveringId) {
      onIsOpenChange(hoveringId === id);
    },
  });

  useEffect(() => {
    setHover(isOpen ? id : (current) => (current === id ? null : current));
  }, [setHover, isOpen, id]);

  const onMouseEnter = isTouchDevice
    ? undefined
    : (e: React.MouseEvent<HTMLElement>) => {
        originalOnMouseEnter(id, e);
      };

  const onMouseLeave = isTouchDevice
    ? undefined
    : () => {
        originalOnMouseLeave(id);
      };

  const [triggerElement, setTriggerElement] = useTriggerElement();

  const onClickOutside = (e: Event) => {
    if (!(e.target instanceof Node) || !triggerElement?.contains(e.target)) {
      setHover(null);
    }
  };

  const anchorElement = providedAnchorElement ?? triggerElement;
  useEffect(() => {
    if (!isOpen || !anchorElement) return undefined;

    const handleScroll = () => setHover(null);

    const ancestors = getOverflowAncestors(anchorElement);
    ancestors.forEach((ancestor) => {
      ancestor.addEventListener('scroll', handleScroll, { passive: true });
    });

    return () => {
      ancestors.forEach((ancestor) => {
        ancestor.removeEventListener('scroll', handleScroll);
      });
    };
  }, [isOpen, anchorElement, setHover]);

  return (
    <>
      <Slot
        ref={setTriggerElement}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={() => {
          if (!triggerElement) return;

          const isInteractive =
            !!triggerElement.querySelector(interactiveElementSelector) ||
            !!triggerElement.closest(interactiveElementSelector);

          // Only open the hover view if the element is not interactive,
          // to avoid conflicting with the element's default behavior
          if (!isInteractive) setHover(id);
        }}
      >
        {children}
      </Slot>
      <ErrorBoundary name="HoverView">
        <Tether
          ref={tetherRef}
          anchorElement={anchorElement}
          positionedView={
            // Keep the view mounted in the DOM to prevent content recreation on re-display.
            // This is particularly beneficial for content with network requests.
            hasBeenOpened && (
              <ClickOutsideHandler
                asChild
                onClickOutside={isOpen ? onClickOutside : undefined}
              >
                <Slot
                  aria-hidden={!isOpen}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  className={classNames(styles.floating, {
                    [styles.isClosed]: !isOpen,
                  })}
                >
                  {view}
                </Slot>
              </ClickOutsideHandler>
            )
          }
          flipBehavior={flipBehavior}
          placement={placement}
          offset={offset}
        />
      </ErrorBoundary>
    </>
  );
};

export default HoverView;
